import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Typography, 
  Space,
  Modal,
  message
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  LockOutlined,
  CheckCircleOutlined,
  BankOutlined,
  BookOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useLoading } from '../../hooks/useLoading';
import { showError } from '../common/ErrorMessage';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

const { Title, Text } = Typography;

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSwitchToLogin }) => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [registeredUsername, setRegisteredUsername] = useState('');
  const { register } = useAuth();
  const [form] = Form.useForm();
  const { loading, withLoading } = useLoading(false);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    message.destroy();
  }, [i18n.language]);

  const getValidationMessage = (type: 'required' | 'minLength' | 'maxLength', field: string, value?: number) => {
    const fieldName = t(`validation.${field}`);
    
    switch (type) {
      case 'required':
        return t('validation.required', { field: fieldName });
      case 'minLength':
        return t('validation.minLength', { field: fieldName, min: value });
      case 'maxLength':
        return t('validation.maxLength', { field: fieldName, max: value });
      default:
        return '';
    }
  };

  const onFinish = async (values: { 
    username: string; 
    email: string; 
    password: string; 
    confirmPassword: string;
    organ_name?: string;
    bubmyeng?: string;
  }) => {
    if (values.password !== values.confirmPassword) {
      message.error(t('auth.passwordMismatch'));
      return;
    }

    await withLoading(async () => {
      try {
        await register(
          values.username, 
          values.email, 
          values.password, 
          values.organ_name, 
          values.bubmyeng
        );
        setRegisteredUsername(values.username);
        setShowSuccessModal(true);
        
        // 폼 리셋
        form.resetFields();
        message.success(t('auth.registerSuccess'));
        
        // 리다이렉트 URL이 있으면 해당 페이지로, 없으면 기본 페이지로 이동
        const redirectTo = searchParams.get('redirect');
        if (redirectTo) {
          navigate(decodeURIComponent(redirectTo));
        } else {
          navigate('/laws');
        }
      } catch (error: any) {
        showError(error, t('auth.registerFailed'));
      }
    });
  };

  const handleSuccessConfirm = () => {
    setShowSuccessModal(false);
    onSwitchToLogin();
  };

  return (
    <>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Card style={{ width: 500, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <Title level={2} style={{ marginBottom: 8 }}>
              {t('auth.register')}
            </Title>
            <Text type="secondary">
              {t('auth.registerTitle')}
            </Text>
          </div>

          <Form
            form={form}
            name="register"
            onFinish={onFinish}
            autoComplete="off"
            layout="vertical"
          >
            <Form.Item
              name="username"
              rules={[
                { required: true, message: getValidationMessage('required', 'username') },
                { min: 3, message: getValidationMessage('minLength', 'username', 3) },
                { max: 50, message: getValidationMessage('maxLength', 'username', 50) }
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder={t('auth.username')}
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="email"
              rules={[
                { required: true, message: getValidationMessage('required', 'emailField') },
                { type: 'email', message: t('validation.email') }
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder={t('auth.email')}
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="organName"
              rules={[
                { required: true, message: getValidationMessage('required', 'organName') },
                { max: 100, message: getValidationMessage('maxLength', 'organName', 100) }
              ]}
            >
              <Input
                prefix={<BankOutlined />}
                placeholder={t('auth.organName')}
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="bubmyeng"
              rules={[
                { required: true, message: getValidationMessage('required', 'bubmyeng') },
                { max: 50, message: getValidationMessage('maxLength', 'bubmyeng', 50) }
              ]}
            >
              <Input
                prefix={<BookOutlined />}
                placeholder={t('auth.bubmyeng')}
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                { required: true, message: getValidationMessage('required', 'password') },
                { min: 6, message: getValidationMessage('minLength', 'password', 6) }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={t('auth.password')}
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: getValidationMessage('required', 'confirmPassword') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('validation.passwordMismatch')));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={t('auth.confirmPassword')}
                size="large"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={loading}
                style={{ width: '100%' }}
              >
                {t('auth.register')}
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center' }}>
            <Space>
              <Text type="secondary">{t('auth.haveAccount')}</Text>
              <Button type="link" onClick={onSwitchToLogin} style={{ padding: 0 }}>
                {t('auth.login')}
              </Button>
            </Space>
          </div>
        </Card>
      </div>

      {/* 회원가입 성공 모달 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px', marginRight: '8px' }} />
            {t('auth.registerComplete')}
          </div>
        }
        open={showSuccessModal}
        onOk={handleSuccessConfirm}
        onCancel={handleSuccessConfirm}
        okText={t('auth.login')}
        cancelText={t('common.close')}
        centered
        closable={true}
        maskClosable={true}
        destroyOnClose={true}
        forceRender={true}
      >
        <div style={{ padding: '16px 0', textAlign: 'center' }}>
          <CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
          <Title level={4} style={{ marginBottom: '8px' }}>
            {t('auth.registerSuccessTitle')}
          </Title>
          <Text type="secondary">
            {t('auth.accountCreated', { username: registeredUsername })}
          </Text>
          <div style={{ marginTop: '16px' }}>
            <Text type="secondary">
              {t('auth.canLoginNow')}
            </Text>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default RegisterForm; 